import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { acceptBAA } from '../../services/settingsService';

const BAA_TEXT = `BUSINESS ASSOCIATE AGREEMENT

This Business Associate Agreement ("BAA") is entered into between Sage Mental Inc. ("Covered Entity / Service Provider") and the practice identified in the Sage account ("Business Associate / Practice"), collectively referred to as the "Parties."

1. DEFINITIONS
Terms used but not otherwise defined in this BAA shall have the same meaning as those terms in the Health Insurance Portability and Accountability Act of 1996 ("HIPAA"), as amended by the Health Information Technology for Economic and Clinical Health ("HITECH") Act, and the regulations promulgated thereunder, including 45 C.F.R. Parts 160 and 164.

2. OBLIGATIONS OF BUSINESS ASSOCIATE
Sage (as a Business Associate) agrees to:
  (a) Not use or disclose Protected Health Information ("PHI") other than as permitted by this BAA or as required by law;
  (b) Use appropriate safeguards, and comply with Subpart C of 45 C.F.R. Part 164 with respect to electronic PHI, to prevent unauthorized use or disclosure of PHI;
  (c) Report to the Practice any use or disclosure of PHI not provided for by this BAA, including Breaches of Unsecured PHI as required by 45 C.F.R. § 164.410, without unreasonable delay and no later than 60 calendar days of discovery;
  (d) Ensure that any subcontractors that create, receive, maintain, or transmit PHI on behalf of Sage agree to the same restrictions and conditions that apply to Sage through a written agreement;
  (e) Make its internal practices, books, and records relating to the use and disclosure of PHI available to the Secretary of HHS for purposes of determining compliance with HIPAA;
  (f) Upon termination of this BAA, return or destroy all PHI received from, or created on behalf of, the Practice.

3. PERMITTED USES AND DISCLOSURES
Sage may use or disclose PHI only to perform services specified in the Terms of Service, or as required by law. Sage may use PHI for its proper management and administration, and to carry out legal responsibilities.

4. OBLIGATIONS OF THE PRACTICE (COVERED ENTITY)
The Practice agrees to:
  (a) Provide Sage with a Notice of Privacy Practices in effect and any changes to such notice;
  (b) Obtain any consent or authorization that may be required by applicable law prior to furnishing Sage with PHI;
  (c) Notify Sage of any restriction on the use or disclosure of PHI that the Practice has agreed to or is required to abide by.

5. TERM AND TERMINATION
This BAA is effective upon electronic acceptance and shall remain in effect until terminated. Either Party may terminate this BAA if the other Party materially breaches any provision and does not cure the breach within 30 days of written notice. Upon termination, Sage will return or destroy all PHI.

6. MISCELLANEOUS
This BAA is governed by the laws of the United States. Any ambiguity shall be resolved in favor of a meaning that permits the Parties to comply with HIPAA. This BAA supersedes any prior agreements between the Parties regarding PHI. Sage reserves the right to amend this BAA as required to comply with changes in applicable law, with notice provided to the Practice.

By accepting below, the authorized representative of the Practice agrees to the terms of this BAA on behalf of their organization.`;

export default function BAAWall({ onAccepted }) {
  const queryClient = useQueryClient();
  const [signerName, setSignerName] = useState('');
  const [agreed, setAgreed] = useState(false);

  const mutation = useMutation({
    mutationFn: () => acceptBAA(signerName),
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', 'practice'], (old) =>
        old ? { ...old, practice: { ...old.practice, ...data.practice } } : old
      );
      queryClient.invalidateQueries({ queryKey: ['settings', 'practice'] });
      toast.success('Business Associate Agreement signed.');
      onAccepted?.();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to record BAA acceptance'),
  });

  const canSubmit = agreed && signerName.trim().length >= 2 && !mutation.isPending;

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-4">
            <ShieldCheckIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            HIPAA Business Associate Agreement
          </h1>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            Required before accessing patient health information. Please review and sign below.
          </p>
        </div>

        {/* BAA text */}
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl p-5 mb-5 max-h-72 overflow-y-auto">
          <pre className="text-xs text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap font-sans leading-relaxed">
            {BAA_TEXT}
          </pre>
        </div>

        {/* Signature section */}
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Full legal name of authorized representative *
            </label>
            <input
              type="text"
              className="input"
              placeholder="e.g. Dr. Jane Smith"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              autoComplete="name"
            />
            <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
              By typing your name you are signing this agreement electronically.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-600"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span className="text-sm text-neutral-700 dark:text-neutral-300">
              I have read and agree to the terms of the Business Associate Agreement on behalf of my practice. I confirm I am authorized to enter into this agreement.
            </span>
          </label>

          <button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="w-full btn btn-primary disabled:opacity-50 py-3"
          >
            {mutation.isPending ? 'Signing…' : 'Sign Business Associate Agreement'}
          </button>

          <p className="text-center text-xs text-neutral-400 dark:text-neutral-500">
            Your acceptance is recorded with your name, timestamp, and IP address for compliance.
          </p>
        </div>
      </div>
    </div>
  );
}
